import React, { useEffect, useState, useCallback } from "react";
import { extractFieldRuleMap } from "../api/mapping-gr-with-protal-fields";

function EligibilityAndRuleError({ eligibilityRules, errors, onClose }) {
  const [formattedErrors, setFormattedErrors] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState({});

  const formatRuleValue = useCallback((value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean).join(", ");
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return String(value ?? "").trim();
  }, []);

  const formatRequirementText = useCallback((fieldName, condition, value) => {
    const normalizedFieldName = String(fieldName || "").trim();
    const normalizedCondition = String(condition || "").trim();
    const formattedValue = formatRuleValue(value);
    const isGenderField = normalizedFieldName.toLowerCase() === "gender";

    if (normalizedCondition === "equals" && formattedValue) {
      return isGenderField
        ? `User must be ${formattedValue}.`
        : `${normalizedFieldName} must be ${formattedValue}.`;
    }

    if (
      (normalizedCondition === "notEquals" ||
        normalizedCondition === "not_equal" ||
        normalizedCondition === "not_equals") &&
      formattedValue
    ) {
      return isGenderField
        ? `User must not be ${formattedValue}.`
        : `${normalizedFieldName} must not be ${formattedValue}.`;
    }

    if (normalizedCondition === "contains" && formattedValue) {
      return `${normalizedFieldName} must include ${formattedValue}.`;
    }

    if (normalizedCondition === "notContains" && formattedValue) {
      return `${normalizedFieldName} must not include ${formattedValue}.`;
    }

    if (normalizedCondition === "isEmpty") {
      return `${normalizedFieldName} must be empty.`;
    }

    if (normalizedCondition === "isNotEmpty") {
      return `${normalizedFieldName} is required.`;
    }

    if (
      normalizedCondition === "notmorethan" ||
      normalizedCondition === "not_more_than" ||
      normalizedCondition === "less_than_or_equal" ||
      normalizedCondition === "less_than_equal" ||
      normalizedCondition === "lessthanorequal"
    ) {
      return `${normalizedFieldName} must be less than or equal to ${formattedValue}.`;
    }

    if (normalizedCondition === "lessthan" || normalizedCondition === "less_than") {
      return `${normalizedFieldName} must be less than ${formattedValue}.`;
    }

    if (normalizedCondition === "greaterthan" || normalizedCondition === "greater_than") {
      return `${normalizedFieldName} must be greater than ${formattedValue}.`;
    }

    if (formattedValue) {
      return `${normalizedFieldName} must satisfy ${normalizedCondition} ${formattedValue}.`;
    }

    return `${normalizedFieldName} failed validation.`;
  }, [formatRuleValue]);

  const formatErrors = useCallback((errorItems, fieldMap) => {
    if (!Array.isArray(errorItems) || errorItems.length === 0) return [];

    return errorItems.reduce((accumulator, errorString) => {
      try {
        const errorObj =
          typeof errorString === "string" ? JSON.parse(errorString) : errorString;

        const errorGroupKey = Object.keys(errorObj).find(
          (key) => key !== "condition" && key !== "message",
        );

        if (!errorGroupKey) {
          return accumulator;
        }

        const fieldIds = errorObj[errorGroupKey];
        const condition = errorObj.condition || "AND";

        if (!Array.isArray(fieldIds)) {
          return accumulator;
        }

        const fieldErrors = fieldIds.map((fieldId) => {
          const fieldRule = fieldMap[fieldId];

          if (fieldRule) {
            const fieldName =
              fieldRule.attributename || fieldRule.finalFieldName || fieldId;

            return {
              fieldId,
              fieldName,
              groupName: fieldRule.groupName,
              condition: fieldRule.condition,
              value: fieldRule.value,
              requirementText: formatRequirementText(
                fieldName,
                fieldRule.condition,
                fieldRule.value,
              ),
            };
          }

          return {
            fieldId,
            fieldName: fieldId,
            requirementText: `${fieldId} failed validation.`,
          };
        });

        accumulator.push({
          group: errorGroupKey,
          condition,
          fieldErrors,
          allConditionsMustBeMet: condition === "AND",
        });
      } catch (error) {
        console.error("Error parsing error item:", error);
      }

      return accumulator;
    }, []);
  }, [formatRequirementText]);

  useEffect(() => {
    try {
      const parsedRules =
        typeof eligibilityRules === "string"
          ? JSON.parse(eligibilityRules)
          : eligibilityRules;

      const fieldMap = extractFieldRuleMap(parsedRules);

      if (errors && errors.length > 0) {
        setFormattedErrors(formatErrors(errors, fieldMap));
      } else {
        setFormattedErrors([]);
      }
    } catch (error) {
      console.error("Error processing data:", error);
      setFormattedErrors([]);
    }

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [eligibilityRules, errors, formatErrors, onClose]);

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1060, backgroundColor: "rgba(0, 0, 0, 0.3)" }}
      />

      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1061 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">Validation Error Details</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                aria-label="Close"
                onClick={onClose}
              />
            </div>

            <div className="modal-body">
              {formattedErrors.length > 0 ? (
                <div className="accordion" id="errorAccordion">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${expandedAccordions.all ? "" : "collapsed"}`}
                        type="button"
                        onClick={() =>
                          setExpandedAccordions((prev) => ({
                            ...prev,
                            all: !prev.all,
                          }))
                        }
                      >
                        <span className="fw-bold">
                          {formattedErrors.length} Error Group{formattedErrors.length !== 1 ? "s" : ""}
                        </span>
                      </button>
                    </h2>
                    <div className={`accordion-collapse collapse ${expandedAccordions.all ? "show" : ""}`}>
                      <div className="accordion-body pt-3">
                        {formattedErrors.map((errorGroup, groupIndex) => (
                          <div key={groupIndex} className="mb-4 border-bottom pb-3">
                            <h6 className="mb-3">
                              <span className="badge bg-primary me-2">{groupIndex + 1}</span>
                              {errorGroup.allConditionsMustBeMet
                                ? "All conditions must be met"
                                : "At least one condition must be met"}
                            </h6>

                            <ol className="ps-4 mb-0">
                              {errorGroup.fieldErrors.map((fieldError, index) => (
                                <li key={index} className="mb-2">
                                  <strong>{fieldError.requirementText || fieldError.fieldName}</strong>
                                  {fieldError.groupName ? (
                                    <span className="badge bg-secondary ms-2">
                                      {fieldError.groupName}
                                    </span>
                                  ) : null}
                                  {fieldError.requirementText ? (
                                    <div className="text-muted small mt-1">
                                      Field: {fieldError.fieldName}
                                    </div>
                                  ) : null}
                                </li>
                              ))}
                            </ol>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-danger" onClick={onClose}>
                Back to Application
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EligibilityAndRuleError;
